import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#5a6268'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${props => props.active ? '#007bff' : 'transparent'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color || '#333'};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #333;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const BannerImage = styled.img`
  width: 150px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #eee;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  background: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.4rem 0.6rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${props => props.edit ? '#007bff' : props.danger ? '#dc3545' : '#28a745'};
  color: white;
  font-size: 0.8rem;

  &:hover {
    opacity: 0.8;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ImageUpload = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

const UploadButton = styled.label`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  color: #333;

  &:hover {
    background: #e9ecef;
  }

  input {
    display: none;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #ddd;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const BannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: '',
    link: '',
    linkType: 'none',
    targetId: '',
    position: 0,
    status: 'active',
    showTitle: true,
    showSubtitle: true,
    interval: 5000
  });
  const [uploading, setUploading] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState([]);

  const fetchBanners = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/banners', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        setBanners(response.data.banners);
      }
    } catch (error) {
      console.error('获取轮播图失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/banners/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image: response.data.url }));
      } else {
        alert(response.data.message || '上传失败');
      }
    } catch (error) {
      console.error('上传图片失败:', error);
      alert('上传图片失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image) {
      alert('请填写标题和上传图片');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingBanner 
        ? `/api/admin/banners/${editingBanner._id}`
        : '/api/admin/banners';
      const method = editingBanner ? 'put' : 'post';
      
      const response = await axios[method](url, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        alert(editingBanner ? '轮播图更新成功' : '轮播图创建成功');
        setShowModal(false);
        resetForm();
        fetchBanners();
      } else {
        alert(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存轮播图失败:', error);
      alert('保存失败，请重试');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      subtitle: banner.subtitle || '',
      image: banner.image,
      link: banner.link || '',
      linkType: banner.linkType || 'none',
      targetId: banner.targetId || '',
      position: banner.position || 0,
      status: banner.status,
      showTitle: banner.showTitle !== false,
      showSubtitle: banner.showSubtitle !== false,
      interval: banner.interval || 5000
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条轮播图吗？')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`/api/admin/banners/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        alert('删除成功');
        fetchBanners();
      } else {
        alert(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除轮播图失败:', error);
      alert('删除失败，请重试');
    }
  };

  const handleToggle = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(`/api/admin/banners/${id}/toggle`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        fetchBanners();
      } else {
        alert(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      alert('操作失败，请重试');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedBanners(banners.map(b => b._id));
    } else {
      setSelectedBanners([]);
    }
  };

  const handleSelectBanner = (id) => {
    setSelectedBanners(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    if (selectedBanners.length === 0) {
      alert('请选择要删除的轮播图');
      return;
    }
    
    if (!window.confirm(`确定要删除选中的 ${selectedBanners.length} 条轮播图吗？`)) return;

    try {
      const response = await axios.post('/api/admin/banners/batch-delete', { ids: selectedBanners });
      if (response.data.success) {
        alert('批量删除成功');
        setSelectedBanners([]);
        fetchBanners();
      } else {
        alert(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      alert('删除失败，请重试');
    }
  };

  const resetForm = () => {
    setEditingBanner(null);
    setFormData({
      title: '',
      subtitle: '',
      image: '',
      link: '',
      linkType: 'none',
      targetId: '',
      position: 0,
      status: 'active',
      showTitle: true,
      showSubtitle: true,
      interval: 5000
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const activeCount = banners.filter(b => b.status === 'active').length;
  const inactiveCount = banners.filter(b => b.status === 'inactive').length;

  return (
    <AdminLayout>
      <Container>
        <Breadcrumb>首页 / 轮播图管理</Breadcrumb>
        
        <Header>
          <Title>轮播图管理</Title>
          <HeaderActions>
            {selectedBanners.length > 0 && (
              <Button danger onClick={handleBatchDelete}>
                批量删除 ({selectedBanners.length})
              </Button>
            )}
            <Button primary onClick={openAddModal}>添加轮播图</Button>
          </HeaderActions>
        </Header>

        <StatsContainer>
          <StatCard active={true}>
            <StatValue>{banners.length}</StatValue>
            <StatLabel>全部轮播图</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#28a745">{activeCount}</StatValue>
            <StatLabel>启用中</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#dc3545">{inactiveCount}</StatValue>
            <StatLabel>已禁用</StatLabel>
          </StatCard>
        </StatsContainer>

        {loading ? (
          <EmptyState>加载中...</EmptyState>
        ) : banners.length === 0 ? (
          <EmptyState>
            <p>暂无轮播图</p>
            <Button primary onClick={openAddModal}>添加第一个轮播图</Button>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th><Checkbox type="checkbox" onChange={handleSelectAll} checked={selectedBanners.length === banners.length && banners.length > 0} /></th>
                <th>图片</th>
                <th>标题</th>
                <th>副标题</th>
                <th>排序</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {banners.map(banner => (
                <tr key={banner._id}>
                  <td><Checkbox type="checkbox" checked={selectedBanners.includes(banner._id)} onChange={() => handleSelectBanner(banner._id)} /></td>
                  <td><BannerImage src={banner.image} alt={banner.title} onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 50"%3E%3Crect fill="%23ddd" width="150" height="50"/%3E%3Ctext x="75" y="30" text-anchor="middle" fill="%23999" font-size="10"%3E图片%3C/text%3E%3C/svg%3E'} /></td>
                  <td>{banner.title}</td>
                  <td>{banner.subtitle || '-'}</td>
                  <td>{banner.position}</td>
                  <td>
                    <StatusBadge active={banner.status === 'active'}>
                      {banner.status === 'active' ? '启用' : '禁用'}
                    </StatusBadge>
                  </td>
                  <td>
                    <ActionButtons>
                      <Button success onClick={() => handleToggle(banner._id)}>
                        {banner.status === 'active' ? '禁用' : '启用'}
                      </Button>
                      <Button primary onClick={() => handleEdit(banner)}>编辑</Button>
                      <Button danger onClick={() => handleDelete(banner._id)}>删除</Button>
                    </ActionButtons>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingBanner ? '编辑轮播图' : '添加轮播图'}</ModalTitle>
                <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
              </ModalHeader>
              
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>标题 *</Label>
                  <Input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="请输入轮播图标题"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>副标题</Label>
                  <Input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    placeholder="请输入副标题（可选）"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>显示设置</Label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <Checkbox
                        type="checkbox"
                        name="showTitle"
                        checked={formData.showTitle}
                        onChange={handleCheckboxChange}
                      />
                      <span>显示标题</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <Checkbox
                        type="checkbox"
                        name="showSubtitle"
                        checked={formData.showSubtitle}
                        onChange={handleCheckboxChange}
                      />
                      <span>显示副标题</span>
                    </label>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>轮播图图片 *</Label>
                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                    建议尺寸：1920×600px (16:5 比例)
                    <br />
                    推荐格式：JPG、PNG、WEBP
                    <br />
                    文件大小：不超过 5MB
                  </div>
                  <ImageUpload>
                    <UploadButton>
                      {uploading ? '上传中...' : '选择图片'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                    </UploadButton>
                    {formData.image && (
                      <>
                        <ImagePreview src={formData.image} alt="预览" />
                        <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem', textAlign: 'center' }}>
                          预览图（后台展示为 150×50px）
                        </div>
                      </>
                    )}
                  </ImageUpload>
                </FormGroup>

                <FormGroup>
                  <Label>链接类型</Label>
                  <Select name="linkType" value={formData.linkType} onChange={handleInputChange}>
                    <option value="none">无链接</option>
                    <option value="product">产品</option>
                    <option value="solution">解决方案</option>
                    <option value="page">页面</option>
                    <option value="external">外部链接</option>
                  </Select>
                </FormGroup>

                {formData.linkType !== 'none' && (
                  <FormGroup>
                    <Label>链接地址</Label>
                    <Input
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      placeholder="请输入链接地址"
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>排序</Label>
                  <Input
                    type="number"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>状态</Label>
                  <Select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="active">启用</option>
                    <option value="inactive">禁用</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>自动轮播间隔（毫秒）</Label>
                  <Input
                    type="number"
                    name="interval"
                    value={formData.interval}
                    onChange={handleInputChange}
                    min="1000"
                    max="30000"
                    step="500"
                  />
                  <span style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    建议设置 3000-8000 毫秒，默认 5000 毫秒
                  </span>
                </FormGroup>

                <ModalFooter>
                  <Button type="button" onClick={() => setShowModal(false)}>取消</Button>
                  <Button type="submit" primary>{editingBanner ? '保存' : '添加'}</Button>
                </ModalFooter>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default BannersPage;
